import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { connect, useDispatch } from 'react-redux';
import { userSelector } from '../../selectors/authSelectors';
import {
  Window,
  WindowHeader,
  WindowContent,
  Table,
  TableHead,
  TableRow,
  TableHeadCell,
  TableBody,
  TableDataCell,
  Hourglass,
  Button,
  Checkbox,
  Select
} from 'react95';
import { lowerCase, sortBy } from 'lodash-es';
import './UserManagement.scss';
import { useNavigate } from 'react-router-dom';
import QDeckToolBar from '../Common/QDeckToolBar';
import ErrorModal from '../Common/ErrorModal';
import { useFetchBookkeepersQuery, useFetchQDeckUsersQuery, useUpdateQDeckUsersMutation } from '../../api/qdeckApi';
import baseApi, { TAGS } from '../../api/baseApi';

const USER_MANAGEMENT_COLUMNS = [
  {
    fieldName: 'email',
    displayName: 'Email',
    type: 'text'
  },
  {
    fieldName: 'firstname',
    displayName: 'First Name',
    type: 'text'
  },
  {
    fieldName: 'lastname',
    displayName: 'Last Name',
    type: 'text'
  },
  {
    fieldName: 'admin',
    displayName: 'Is Admin?',
    type: 'check'
  },
  {
    fieldName: 'eng',
    displayName: 'Is Engineer?',
    type: 'check'
  }
];

const DEFAULT_NEW_USER = {
  bookkeeper_id: undefined,
  email: null,
  firstname: undefined,
  lastname: undefined,
  admin: 0,
  eng: 0,
  new: true
};

const UserManagement = ({ user }) => {
  const [updatedQDeckUsers, setUpdatedQDeckUsers] = useState([]);
  const [hasError, setHasError] = useState(false);

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { data: qdeckUsers = [], isLoading: areQDeckUsersLoading } = useFetchQDeckUsersQuery();
  const { data: bookkeepers = [], isLoading: areBookkeepersLoading } = useFetchBookkeepersQuery();
  const [updateQDeckUsers, { error, isError }] = useUpdateQDeckUsersMutation();

  useEffect(() => {
    if (isError) {
      setHasError(true);
    }
  }, [isError]);

  const hasChanged = useMemo(() => {
    return updatedQDeckUsers.length !== qdeckUsers.length;
  }, [updatedQDeckUsers, qdeckUsers]);

  const getAvailableNewUsers = (currentIndex) => {
    const availableNewUsers = bookkeepers
      .filter(
        (bookkeeper) =>
          !updatedQDeckUsers.some((qdeckUser, index) => qdeckUser.email === bookkeeper.email && index !== currentIndex)
      )
      .map((bookkeeper) => bookkeeper.email)
      .sort()
      .map((email) => ({ label: email, value: email }));
    return [{ label: '', value: null }, ...availableNewUsers];
  };

  const allUsersHaveEmail = useMemo(() => {
    return updatedQDeckUsers.every((user) => user.email);
  }, [updatedQDeckUsers]);

  useEffect(() => {
    setUpdatedQDeckUsers(sortBy(qdeckUsers, (user) => lowerCase(user.email)));
  }, [qdeckUsers]);

  const updateField = (targetIdx, field, value) => {
    setUpdatedQDeckUsers(
      updatedQDeckUsers.map((qdeckUser, idx) => (idx === targetIdx ? { ...qdeckUser, [field]: value } : qdeckUser))
    );
  };

  const addUser = () => {
    setUpdatedQDeckUsers([{ ...DEFAULT_NEW_USER }, ...updatedQDeckUsers]);
  };

  const updateNewUserEmail = (targetIdx, email) => {
    const bookkeeper = bookkeepers.find((bookkeeper) => bookkeeper.email === email);
    if (!bookkeeper) {
      setUpdatedQDeckUsers(
        updatedQDeckUsers.map((qdeckUser, idx) => (idx === targetIdx ? DEFAULT_NEW_USER : qdeckUser))
      );
    } else {
      setUpdatedQDeckUsers(
        updatedQDeckUsers.map((qdeckUser, idx) =>
          idx === targetIdx
            ? {
                ...qdeckUser,
                email: bookkeeper.email,
                bookkeeper_id: bookkeeper.id,
                firstname: bookkeeper.firstname,
                lastname: bookkeeper.lastname
              }
            : qdeckUser
        )
      );
    }
  };

  const removeUser = (targetIdx) => {
    setUpdatedQDeckUsers([...updatedQDeckUsers.slice(0, targetIdx), ...updatedQDeckUsers.slice(targetIdx + 1)]);
  };

  const submitChanges = () => {
    updateQDeckUsers(updatedQDeckUsers);
  };

  const onTaskListClick = useCallback(() => navigate('/task-list'), [navigate]);

  const onCloseErrorModal = () => setHasError(false);

  const refreshBtn = {
    name: 'Refresh',
    onClick: dispatch(baseApi.util.invalidateTags([TAGS.QDECK_USERS]))
  };

  const taskListBtn = {
    name: 'Task List',
    onClick: onTaskListClick
  };

  const isLoading = areQDeckUsersLoading || areBookkeepersLoading;

  return (
    <div className='user-management'>
      {hasError && <ErrorModal errorMsg={error} onClose={onCloseErrorModal} />}
      <QDeckToolBar type='task detail' menuItems={[taskListBtn, refreshBtn]} />
      <Window className='user-management-window'>
        <WindowHeader>
          <span>Manage Users</span>
          {isLoading && <Hourglass size={24} />}
        </WindowHeader>
        <WindowContent>
          <div className='user-management-actions'>
            <Button onClick={addUser} disabled={!allUsersHaveEmail}>
              Add User
            </Button>
            <Button disabled={!hasChanged || !allUsersHaveEmail} onClick={submitChanges}>
              Submit Changes
            </Button>
            {!allUsersHaveEmail && <span>Please assign an email to all users</span>}
          </div>
          <div className='user-management-table-container'>
            <Table>
              <TableHead>
                <TableRow>
                  {USER_MANAGEMENT_COLUMNS.map(({ displayName, fieldName }) => (
                    <TableHeadCell key={fieldName}>
                      <span>{displayName}</span>
                    </TableHeadCell>
                  ))}
                  <TableHeadCell>
                    <span>Remove</span>
                  </TableHeadCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {updatedQDeckUsers.map((qdeckUser, idx) => (
                  <TableRow key={qdeckUser.email}>
                    {USER_MANAGEMENT_COLUMNS.map(({ fieldName, type }) => (
                      <TableDataCell key={fieldName}>
                        {type === 'check' && (
                          <Checkbox
                            checked={qdeckUser[fieldName]}
                            onClick={() => updateField(idx, fieldName, !qdeckUser[fieldName])}
                          />
                        )}
                        {(fieldName !== 'email' || !qdeckUser.new) && type !== 'check' && (
                          <div>{qdeckUser[fieldName]}</div>
                        )}
                        {fieldName === 'email' && qdeckUser.new && (
                          <Select
                            value={qdeckUser[fieldName]}
                            options={getAvailableNewUsers(idx)}
                            width={250}
                            menuMaxHeight={300}
                            onChange={({ value: email }) => {
                              updateNewUserEmail(idx, email);
                            }}
                          />
                        )}
                      </TableDataCell>
                    ))}
                    <TableDataCell>
                      {user.email !== qdeckUser.email && (
                        <Button onClick={() => removeUser(idx)}>
                          <span className='remove-user-text'>X</span>
                        </Button>
                      )}
                    </TableDataCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </WindowContent>
      </Window>
    </div>
  );
};

const mapStateToProps = (state) => ({
  user: userSelector(state)
});

export default connect(mapStateToProps, null)(UserManagement);
